import React, { useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Paper,
    Stack,
    IconButton,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as XLSX from "xlsx";

const UploadDomainModal = ({
    open,
    handleClose,
    onUpload,
}) => {

    const fileInputRef = useRef();
    const [selectedFile, setSelectedFile] = useState(null);

    const handleBrowse = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    };

    const onCloseModal = () => {
        setSelectedFile(null);

        // Reset the file input as well
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        handleClose();
    };

    const downloadTemplate = () => {
        const worksheet = XLSX.utils.aoa_to_sheet([
            ["Domain Name"]
        ]);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

        XLSX.writeFile(workbook, "Domain_Template.xlsx");
    };

    const handleUpload = () => {
        if (!selectedFile) return;
        onUpload(selectedFile);
        setSelectedFile(null);
        onCloseModal();
    };

    return (
        <Dialog
            open={open}
            onClose={onCloseModal}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 700,
                }}
            >
                Upload Domains via Excel
                <IconButton onClick={onCloseModal}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography
                    color="text.secondary"
                    mb={3}
                >
                    Upload a CSV or Excel file containing
                    Domain names.
                    The first column must contain the domain.
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        borderStyle: "dashed",
                        borderWidth: 2,
                        borderRadius: 3,
                        p: 5,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: "#fafafa",
                    }}
                    onClick={handleBrowse}
                >

                    <CloudUploadOutlinedIcon
                        sx={{
                            fontSize: 60,
                            color: "#1976d2",
                        }}
                    />

                    <Typography
                        mt={2}
                        fontWeight={600}
                    >
                        Click to browse
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        or drag & drop
                    </Typography>

                    <Typography
                        mt={1}
                        fontSize={12}
                        color="text.secondary"
                    >
                        Supports .csv, .xls, .xlsx
                    </Typography>

                    <input
                        hidden
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xls,.xlsx"
                        onChange={handleFileChange}
                    />

                </Paper>

                {selectedFile && (
                    <Alert
                        severity="success"
                        sx={{ mt: 2 }}
                    >
                        {selectedFile.name}
                    </Alert>
                )}
                <Box
                    mt={2}
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: 2,
                        border: "1px solid #CFE3FF",
                        backgroundColor: "#F5FAFF",
                        borderRadius: 2,
                    }}
                >
                    <InfoOutlinedIcon
                        sx={{
                            color: "#1976d2",
                            fontSize: 18,
                            mt: "2px",
                        }}
                    />

                    <Box flex={1}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#4b5563",
                                lineHeight: 1.6,
                            }}
                        >
                            <strong>Expected format:</strong>{" "}
                            Column header{" "}
                            <Box
                                component="span"
                                sx={{
                                    bgcolor: "#EEF4FF",
                                    border: "1px solid #D6E4FF",
                                    borderRadius: 1,
                                    px: 0.5,
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}
                            >
                                Domain Name
                            </Box>{" "}
                            in row 1, one domain per row below (e.g.{" "}
                            <strong>supplier.com</strong>).
                        </Typography>

                        <Button
                            size="small"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={downloadTemplate}
                            sx={{
                                mt: 1,
                                p: 0,
                                minWidth: 0,
                                textTransform: "none",
                                color: "#1976d2",
                                fontWeight: 600,
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#1976d2",
                                },
                            }}
                        >
                            Download Template
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onCloseModal}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                    sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        textTransform: "none",
                        "&:hover": {
                            backgroundColor: "#000",
                            color: "#fff",
                            boxShadow: "none",
                        },
                    }}
                >
                    Upload Domains
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadDomainModal;